import CustomPhoneInput from '@components/book-trial-class/phone-input'
import classNames from 'classnames'
import React, { FormEvent, useEffect, useMemo, useState } from 'react'
import TermsLabelComponent from '@components/book-trial-class/terms-label'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import MsgTrans from '@components/common/i18n/message-trans'
import { childGradeOptions } from '@lib/enums/registration-form'
import { Trans, t } from '@lingui/macro'
import { Icon } from '@components/common/icons'
import { GradeType } from '@components/common/forms/grade-select'
import { IdentifierMode } from '@lib/utils/identifier'
import minimatch from 'minimatch'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { FastlyImage } from '@components/common/image'
import { ChevronDown } from 'react-feather'
import OutsideClickHandler from 'react-outside-click-handler'
const DynamicReactSelectGrade = dynamic(() =>
  import('@components/common/forms/select')
)
const DynamicCustomGradeSelect = dynamic(() =>
  import('@components/common/forms/grade-select')
)

const FORM_SHADOW = {
  boxShadow:
    '0px 0.9px 3.2px rgba(0, 0, 0, 0.028), 0px 2.5px 8.8px rgba(0, 0, 0, 0.04), 0px 6px 21.1px rgba(0, 0, 0, 0.052), 0px 20px 70px rgba(0, 0, 0, 0.08)',
}

const SubjectSelect = ({
  options,
  onChange,
  errorText,
}: {
  options: { label: string; slug: string }[]
  onChange?: (slug: string) => void
  errorText?: string
}) => {
  const [showOptions, setShowOptions] = useState(false)
  const [selected, setSelected] = useState<{
    slug: string
    label: string
  } | null>(null)

  const handleSelect = (option: { slug: string; label: string }) => {
    setSelected(option)
    setShowOptions(false)
    onChange?.(option.slug)
  }

  return (
    <OutsideClickHandler onOutsideClick={() => setShowOptions(false)}>
      <div className="relative w-full select-none">
        <div
          className="flex items-center justify-between text-grey rounded px-4 py-2 bg-white cursor-pointer border border-grey-500 hover:border hover:border-grey"
          onClick={() => setShowOptions(!showOptions)}
        >
          {selected?.label || 'Select subject'}
          <ChevronDown size={15} />
        </div>

        {showOptions && (
          <div className="absolute top-0 left-0 w-full mt-10 bg-white rounded border border-grey-500 overflow-y-auto z-10">
            {options.map((option) => (
              <div
                key={option.slug}
                className={classNames('cursor-pointer', {
                  'bg-orange-500 text-white': selected?.slug === option.slug,
                  'hover:bg-orange-200': selected?.slug !== option.slug,
                })}
                onClick={() => handleSelect(option)}
              >
                <p className="px-4 py-2">{option.label}</p>
              </div>
            ))}
          </div>
        )}
        {errorText && (
          <div className="py-2 text-sm text-red-200 font-400">{errorText}</div>
        )}
      </div>
    </OutsideClickHandler>
  )
}

type Props = {
  phone: string
  // Opt-in: when false the field is phone-only, exactly as it has always been.
  // Landing pages stay opted out until their region gate lands.
  allowEmail?: boolean
  identifierMode?: IdentifierMode
  name: string
  email: string
  role: string
  childName: string
  childGrade: { value: string; label: string }
  childGender: { value: string; label: string }
  loading: boolean
  errors: any
  LOGGEDIN_PROMO_PAGE: boolean
  handlePhoneInputChange: (value: string, mode: IdentifierMode) => void
  handleSelectChange: (
    id: string,
    obj: {
      value: string
      label: string
    }
  ) => void
  handleTrialBookingFormSubmit: (e: FormEvent<HTMLFormElement>) => void
  ctaText?: string | LinguiJSMessageDescriptor
  termsAndConditions?: string
  countryCode?: string
  shouldHaveFormShadow?: boolean
  formHeading?: string
  phoneFieldLabel?: string | LinguiJSMessageDescriptor
  isRightToLeftPage?: boolean
  handleGradeSelect: (grade: GradeType) => void
  isAutoRegister?: boolean
  trialSubject?: string
  subjectDropdown?: boolean
  subjectOptions?: { label: string; slug: string }[]
  onSubjectChange?: (slug: string) => void
  subjectError?: string
}
const ONE_SECOND_IN_MILLISECONDS = 1000
const DELAY_BEFORE_REDIRECT_IN_MILLISECONDS = 10000

export const BookTrialClassForm = ({
  phone,
  allowEmail = false,
  identifierMode = 'phone',
  childGrade,
  loading,
  errors,
  LOGGEDIN_PROMO_PAGE,
  handlePhoneInputChange,
  handleSelectChange,
  handleTrialBookingFormSubmit,
  ctaText = t`Try a free lesson`,
  termsAndConditions,
  countryCode,
  shouldHaveFormShadow = false,
  formHeading = '',
  phoneFieldLabel,
  isRightToLeftPage,
  handleGradeSelect,
  isAutoRegister,
  trialSubject,
  subjectDropdown = false,
  subjectOptions = [],
  onSubjectChange,
  subjectError,
}: Props) => {
  const router = useRouter()
  const formRef = React.createRef<HTMLFormElement>()
  const [hideform, setHideForm] = useState(false)
  const [timeLeft, setTimeLeft] = useState(
    DELAY_BEFORE_REDIRECT_IN_MILLISECONDS
  )

  // Focus phone input on page load
  useEffect(() => document.getElementById('phone').focus(), [])

  const fieldErrors = useMemo(() => {
    const phone = errors?.phone
    const childGrade = errors?.childGrade
    setHideForm(false)
    return { phone, childGrade }
  }, [errors])

  useEffect(() => {
    if (isAutoRegister && formRef.current) {
      setHideForm(true)
      const timer = setTimeout(() => {
        document.getElementById('submitBtn')?.click()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isAutoRegister, formRef.current])

  useEffect(() => {
    import('@common/utils').then(
      ({ flattenErrorsDictValues, showErrorAlert }) => {
        const formErrors = errors
        delete formErrors.phone
        delete formErrors.childGrade

        const flattenedErrors = flattenErrorsDictValues(formErrors)
        if (flattenedErrors.length > 0)
          showErrorAlert({ text: flattenedErrors.join('\n') })
      }
    )
  }, [errors])

  useEffect(() => {
    setTimeout(() => {
      if (timeLeft > 0) setTimeLeft(timeLeft - 100)
      if (timeLeft === 0) {
        return
      }
    }, 100)
  }, [timeLeft])

  useEffect(() => {
    if (timeLeft === 0 && !loading) {
      setHideForm(false)
    }
  }, [timeLeft, loading])

  // Only shows custom grade select component on lp pages for LCP improvement
  // TODO: add this component in every form replacing the `react-select` framework
  const shouldShowCustomGradeComponent = () => {
    const routes = ['/lp/**', '/book-trial-class', '/book-trial-class/**']
    const path = router?.asPath
    return routes.some((glob) => minimatch(path, glob))
  }

  return (
    <>
      {hideform && (
        <div className="flex flex-col max-w-sm px-8 lg:max-w-lg text-center lg:text-left">
          <FastlyImage
            src="/images/trial-class/track-booking/penguin.svg"
            alt="Penguin"
            className="h-40 lg:h-96 w-auto"
            width={100}
            height={218}
          />
          <p className="text-orange text-lg md:text-3xl font-600">
            <Trans>Registration in progress</Trans>
          </p>
          <p className="text-grey">
            <Trans>We will take you to the dashboard in few seconds</Trans>
          </p>
          <div className="mt-1">
            <div className="bg-grey-400 h-5 w-full rounded-2xl">
              <div
                className="h-full bg-orange rounded-2xl transform transition-all duration-100 ease-in"
                style={{
                  minWidth: '5%',
                  width: `${
                    10 *
                    ((DELAY_BEFORE_REDIRECT_IN_MILLISECONDS - timeLeft) /
                      ONE_SECOND_IN_MILLISECONDS)
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <form
        ref={formRef}
        className={`z-10 ${
          hideform ? 'hidden' : 'flex'
        } flex-col w-full px-3 py-8 space-y-4 bg-white rounded-lg lg:px-5 lg:w-108`}
        style={shouldHaveFormShadow ? FORM_SHADOW : {}}
        onSubmit={handleTrialBookingFormSubmit}
      >
        {!formHeading && (
          <p className="text-center text-grey">
            {t`Hurry Up! The slots are filling fast`}
          </p>
        )}
        {formHeading && (
          <h1 className="text-4xl leading-tight text-grey lg:text-5xl font-600">
            {formHeading}
          </h1>
        )}
        <CustomPhoneInput
          value={phone}
          mode={allowEmail ? identifierMode : 'phone'}
          allowEmail={allowEmail}
          onChange={handlePhoneInputChange}
          label={
            allowEmail
              ? t({
                  id: 'register.identifier.title',
                  message: `Enter your phone number or email`,
                })
              : (phoneFieldLabel as any)
          }
          phoneLabel={
            allowEmail
              ? t({
                  id: 'register.identifier.title.phone',
                  message: `Enter your phone number`,
                })
              : undefined
          }
          emailLabel={
            allowEmail
              ? t({
                  id: 'register.identifier.title.email',
                  message: `Enter your email address`,
                })
              : undefined
          }
          id="phone"
          name="phone"
          required={true}
          autoFocus={false}
          placeholder={
            allowEmail
              ? t({
                  id: 'register.identifier.label',
                  message: `Phone number or email`,
                })
              : t({
                  id: 'register.phone.label',
                  message: `Enter your mobile number`,
                })
          }
          errorText={fieldErrors?.phone}
          disabled={LOGGEDIN_PROMO_PAGE}
          countryCode={countryCode}
          isRightToLeftPage={isRightToLeftPage}
        />

        {!shouldShowCustomGradeComponent() && (
          <div className="flex flex-col gap-y-4 lg:gap-y-1 lg:flex-row lg:gap-x-4">
            <div className="w-full">
              <DynamicReactSelectGrade
                id="childGrade"
                value={childGrade}
                handleSelectChange={handleSelectChange}
                options={childGradeOptions}
                errorText={fieldErrors?.childGrade}
                disabled={LOGGEDIN_PROMO_PAGE}
                isSearchable={false}
                required
              />
            </div>
          </div>
        )}

        {shouldShowCustomGradeComponent() && (
          <div className="flex flex-col gap-y-4 lg:gap-y-1 lg:flex-row lg:gap-x-4">
            <div className="w-full">
              <DynamicCustomGradeSelect
                handleGradeSelect={handleGradeSelect}
                errorText={fieldErrors?.childGrade}
              />
            </div>
          </div>
        )}

        {subjectDropdown && (
          <SubjectSelect
            options={subjectOptions}
            onChange={onSubjectChange}
            errorText={subjectError}
          />
        )}
        <div>
          <div className="my-4">
            <button
              id="submitBtn"
              className={classNames('btn btn-orange w-full py-3 text-lg', {
                'opacity-50 cursor-not-allowed': loading,
              })}
            >
              <MsgTrans msg={ctaText} />
            </button>
          </div>
          <div className="flex items-start justify-center my-3 text-grey">
            <Icon name="Edit3" size={16} className="text-orange mr-2 mt-1" />
            {!trialSubject && (
              <p className="text-sm">
                {t`1000+ slots booked in last 24 hours`}
              </p>
            )}
            {trialSubject === 'Math' && (
              <p className="text-sm">{t`Register now, grab your free slot for math class!`}</p>
            )}
            {trialSubject === 'Public Speaking & Communication' && (
              <p className="text-sm">{t`Register now, grab your free slot for English class!`}</p>
            )}
            {trialSubject === 'Coding' && (
              <>
                {' '}
                {!formHeading && (
                  <p className="text-sm">
                    {t`1000+ slots booked in last 24 hours`}
                  </p>
                )}
                {formHeading && (
                  <p className="text-md">{t`Register now, grab your free slot for coding class!`}</p>
                )}
              </>
            )}
          </div>
          {termsAndConditions ? (
            <p
              className="text-sm text-grey-800"
              dangerouslySetInnerHTML={{ __html: termsAndConditions }}
            ></p>
          ) : (
            <TermsLabelComponent />
          )}
        </div>
      </form>
    </>
  )
}

export default React.memo(BookTrialClassForm)
